import { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';

import {
  CoinsIcon,
  CurrencyExchangeIcon,
  EyeIcon,
  UsersIcon,
} from '../assets/Icon';
import PayoutModal from '../components/dialogs/payout-dialog';
import SettlementDetailsModal from '../components/dialogs/settlement-details-dialog';
import TableComponent from '../components/Table';
import CustomDateRangePicker from '../components/common/DatePicker/CustomDateRangePicker';
import TransactionLink from '../components/ui/TransactionLink';

import AuthContext from '../contexts/AuthContext';
import useAPI from '../hooks/useAPI';
import { dateFormate, toUpperCase } from '../lib/constant';
import { PaginatedDataEntity } from '../lib/types/Settlement';
import { formatCurrency } from '../utility/number.utils';
import styles from './Settlement.module.scss';
import { convertToCSV } from '../lib/lib';
import apiInstance from '../config/axios';

interface IPaginationObject {
  limit: number;
  page: number;
  status: string;
  from: string;
  to: string;
}

interface ISettlementResponse {
  result: {
    paginatedData: PaginatedDataEntity[];
    totaldata: number;
    totalPages: number;
  };
}

const StatusOptions = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Settled', value: 'SUCCESS' },
  { label: 'Rejected', value: 'FAILED' },
];

const MAX_DATE_RANGE_DAYS = 90;

export default function Settlement() {
  const { adminBalance, refetchAdminBalance } = useContext(AuthContext);
  const [isDownloading, setIsDownloading] = useState(false);

  // Pagination and filter states
  const [paginationData, setPaginationData] = useState<IPaginationObject>({
    page: 1,
    limit: 10,
    status: '',
    from: '',
    to: '',
  });

  // Date range states
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<string>('');

  // Modal states
  const [showPayoutModal, setShowPayoutModal] =
    useState<PaginatedDataEntity | null>(null);
  const [showDetailsModal, setShowDetailsModal] =
    useState<PaginatedDataEntity | null>(null);

  // Build API URL with query parameters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationData.page));
    params.append('limit', String(paginationData.limit));
    if (paginationData.status) {
      params.append('status', paginationData.status);
    }
    if (paginationData.from) {
      params.append('fromDate', paginationData.from);
    }
    if (paginationData.to) {
      params.append('toDate', paginationData.to);
    }
    return `/withdrawrequest/list?${params.toString()}`;
  };

  // Fetch settlement data using useAPI
  const {
    data: withdrawalResponse,
    refetch,
    isLoading,
  } = useAPI<void, ISettlementResponse>({
    url: buildApiUrl(),
    method: 'GET',
    dependencies: [
      paginationData.page,
      paginationData.limit,
      paginationData.status,
      paginationData.from,
      paginationData.to,
    ],
    onSuccess: () => {},
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch settlement data',
      );
    },
  });

  const settlementData = (withdrawalResponse?.result?.paginatedData || []).map(
    (item: any) => ({
      ...item,
      transactionId: item.transactionId?.split(': ').pop(),
    }),
  );
  const totalCount = withdrawalResponse?.result?.totaldata || 0;

  // Validate date range (max 90 days)
  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined,
  ): boolean => {
    if (!start || !end) return true;

    const diffInDays = moment(end).diff(moment(start), 'days');
    if (diffInDays > MAX_DATE_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return false;
    }
    setDateError('');
    return true;
  };

  // Handle date range change with validation
  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    if (startDate && date) {
      if (validateDateRange(startDate, date)) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          from: moment(startDate).format('YYYY-MM-DD'),
          to: moment(date).format('YYYY-MM-DD'),
        }));
      }
    } else if (!date) {
      setDateError('');
      setPaginationData(prev => ({
        ...prev,
        page: 1,
        from: '',
        to: '',
      }));
    }
  };

  // Handle status filter change
  const handleStatusChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      status: value === 'all' ? '' : value,
    }));
  };

  // Handle pagination change from TableComponent
  const handleFetchMoreData = (pageSize: number, page: number) => {
    setPaginationData(prev => ({
      ...prev,
      limit: pageSize,
      page: page,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setDateRange([null, null]);
    setStartDate(undefined);
    setEndDate(undefined);
    setDateError('');
    setPaginationData({
      page: 1,
      limit: 10,
      status: '',
      from: '',
      to: '',
    });
  };

  useEffect(() => {
    refetchAdminBalance();
  }, [refetchAdminBalance]);

  const columns = [
    {
      label: 'Name',
      id: 'createdBy',
      width: 150,
      renderCell: (row: any) => (
        <span>
          {row?.createdBy?.firstName} {row?.createdBy?.lastName}
        </span>
      ),
    },
    {
      label: 'Role',
      id: 'role',
      renderCell: (row: any) => (
        <span>{toUpperCase(row?.createdBy?.role)}</span>
      ),
    },
    {
      label: '# of Tokens',
      id: 'amount',
      renderCell: (row: any) =>
        formatCurrency(row?.amount, { currency: '', decimals: 0 }),
    },
    {
      label: 'Txn number',
      id: 'tokenTransactions',
      renderCell: (row: any) => {
        return row?.tokenTransactions?.transactionNo ? (
          <TransactionLink
            transactionNo={row?.tokenTransactions?.transactionNo}
          />
        ) : (
          '-'
        );
      },
    },
    {
      label: 'Mode',
      id: 'transactionsMode',
      renderCell: (row: any) => toUpperCase(row?.transactionsMode),
    },
    {
      label: 'Status',
      id: 'status',
      renderCell: (row: any) => (
        <span
          className={
            row?.status?.toLowerCase() === 'pending'
              ? 'text-danger'
              : row?.status?.toLowerCase() === 'failed'
                ? 'text-danger'
                : 'text-success'
          }
        >
          {toUpperCase(
            row?.status?.toLowerCase() === 'pending'
              ? 'Pending'
              : row?.status?.toLowerCase() === 'failed'
                ? 'Rejected'
                : 'Settled',
          )}
        </span>
      ),
    },
    {
      label: 'Txn Date',
      id: 'createdAt',
      renderCell: (row: any) =>
        row?.createdAt ? dateFormate(row.createdAt) : '-',
    },
    {
      label: 'Action',
      id: 'action',
      renderCell: (row: any) => {
        return (
          <div className="d-flex">
            {row?.status?.toLowerCase() === 'pending' && (
              <Button
                variant="plain"
                type="button"
                onClick={() => setShowPayoutModal(row)}
              >
                <CurrencyExchangeIcon color="#666666" />
              </Button>
            )}
            <Button
              variant="plain"
              type="button"
              onClick={() => setShowDetailsModal(row)}
            >
              <EyeIcon color="#666666" />
            </Button>
          </div>
        );
      },
    },
  ];

  const hasActiveFilters =
    paginationData.status || paginationData.from || paginationData.to;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', paginationData.status || '');
      params.append('fromDate', paginationData.from);
      params.append('toDate', paginationData.to);

      const response = await apiInstance.get(
        `/withdrawrequest/download?${params.toString()}`,
      );

      const transactionHistory = response.data.result.requestedData;

      if (!transactionHistory || transactionHistory.length === 0) {
        toast.error('No data available to download');
        return;
      }

      const csvContent = convertToCSV(transactionHistory);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = moment().format('DDMMYYYYHHmmss');
      link.setAttribute('download', `Settlement${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(error?.response?.data?.message || 'Failed to download CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between">
        <div>
          <h2 className="fw-bold">Settlement</h2>
        </div>
      </div>

      <Row className={`mt-2 mb-3 ${styles.statsRow}`}>
        <Col xs={12} lg={4}>
          <div
            className={`px-5 py-3 bg-white rounded-3 d-flex justify-content-center align-items-center gap-2 ${styles.statCard}`}
          >
            <CoinsIcon color="var(--primary)" size={40} />
            <p className="h1 fw-bold">{adminBalance?.successWithdrawCount}</p>
            <h5>Total Settlement</h5>
          </div>
        </Col>
        <Col xs={12} lg={4}>
          <div
            className={`px-5 py-3 bg-white rounded-3 d-flex justify-content-center align-items-center gap-2 ${styles.statCard}`}
          >
            <UsersIcon color="var(--primary)" size={40} />
            <p className="h1 fw-bold">{adminBalance?.totalUserCount}</p>
            <h5>Total Users</h5>
          </div>
        </Col>
        <Col xs={12} lg={4}>
          <div
            className={`px-5 py-3 bg-white rounded-3 d-flex justify-content-center align-items-center gap-2 ${styles.statCard}`}
          >
            <CurrencyExchangeIcon color="var(--primary)" size={40} />
            <p className="h1 fw-bold">{adminBalance?.pendingWithdrawCount}</p>
            <h5>Pending Amount</h5>
          </div>
        </Col>
      </Row>

      <div className="bg-white rounded-3 p-3">
        {/* Filters Section */}
        <div className="d-flex flex-wrap gap-3 mb-3 align-items-end">
          {/* Date Range Picker */}
          <div style={{ minWidth: '220px' }} className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">
              Transaction Date <span className="text-muted fw-normal"></span>
            </Form.Label>
            <CustomDateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              // @ts-ignore
              setEndDate={handleEndDateChange}
              placeholder="Select date range"
            />
            {dateError && (
              <Form.Text className="text-danger small">{dateError}</Form.Text>
            )}
          </div>

          {/* Status Filter */}
          <div style={{ minWidth: '150px' }}>
            <Form.Label className="small fw-semibold mb-1">Status</Form.Label>
            <Form.Select
              className="rounded-2 custom-input"
              value={paginationData.status || 'all'}
              onChange={e => handleStatusChange(e.target.value)}
            >
              <option value="all">All Status</option>
              {StatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="d-flex flex-column">
              <Form.Label className="small fw-semibold mb-1 custom-filter">
                {/* <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>
              
            </span> */}
              </Form.Label>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleClearFilters}
                className="rounded-2 custom-filter-button"
              >
                Clear Filters
              </Button>
            </div>
          )}

          {/* Download Button */}
          <div className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">&nbsp;</Form.Label>
            <Button
              className={styles.actionButton}
              onClick={handleDownload}
              disabled={isDownloading}
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </div>
        </div>

        {/* Settlement Table */}
        <div className={styles.tableWrapper}>
          <TableComponent
            data={settlementData}
            columns={columns}
            totalCount={totalCount}
            fetchMore={handleFetchMoreData}
            isServerSidePagination
            perPageSize={paginationData.limit}
            activePage={paginationData.page}
            loading={isLoading}
          />
        </div>
      </div>

      <PayoutModal
        open={!!showPayoutModal}
        onClose={() => {
          setShowPayoutModal(null);
          refetchAdminBalance();
        }}
        data={showPayoutModal}
        refetch={refetch}
      />
      <SettlementDetailsModal
        open={!!showDetailsModal}
        onClose={() => setShowDetailsModal(null)}
        data={showDetailsModal}
      />
    </>
  );
}
